'use client';

import styles from './OrderStatusSummary.module.css';
import type { OrderStatusSummaryProps } from './types';
import { PixumEnvUtils } from 'utils';
import Text, { TEXT_VARIANT } from '@pixum/text';
import { OrderStatusSummaryCard } from '../OrderStatusSummaryCard';
import { useMemo } from 'react';
import { useUIStore, FormStatus } from 'stores/ui';
import { ORDER_STATUS, SHIPPED_STATES } from '../../constants';

/**
 * OrderStatusSummary component - displays order summary with grid-based layout
 * Server Component - renders order details with progress indicator
 *
 * Features:
 * - Grid-based responsive layouts matching Gatsby design
 * - SVG progress bar with three stages
 * - Order header with grid (3 columns on desktop)
 * - Product groups with nested structure
 * - Delivery date ranges with formatting
 * - Tracking information with external links
 * - Sub-orders displayed as cards
 * - Handles INIT, SHIPPED, and DELIVERED orders without subOrders
 *
 * Accessibility Features:
 * - ARIA region landmarks
 * - Semantic heading hierarchy
 * - Descriptive labels for interactive elements
 * - External link indicators
 * - Proper list semantics
 */
export function OrderStatusSummary({
  order,
  orderInfo,
  constants,
  productConstants,
  summaries,
  theme,
}: OrderStatusSummaryProps) {
  const locale = PixumEnvUtils.get('LOCALE') || 'en-GB';
  const { setContactFormStatus, setContactFormSubjectToMissingOrder, setContactMessageToSample } = useUIStore(
    state => state,
  );

  const summaryHeadline = useMemo(() => {
    const formattedDate = order.orderDate ? new Date(order.orderDate).toLocaleDateString(locale) : null;

    return formattedDate
      ? `${constants['i18n_order-status_order-headline_suborders']} ${formattedDate}:`
      : constants['i18n_order-status_order-headline'];
  }, [order, constants]);

  /**
   * Handles the contact button click for unknown order status
   * This function:
   * 1. Sets the contact form message with the order ID
   * 2. Shows the contact form
   * 3. Scrolls to the customer support section
   * 4. Sets the subject to "missing order" (delayed to allow form to mount)
   */
  const handleContactClick = () => {
    if (order?.id) {
      setContactMessageToSample(order.id.toString());
    }

    setContactFormStatus(FormStatus.visible);

    const customerSupportSection = document.getElementById('customer-support');

    if (customerSupportSection) {
      customerSupportSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    // Delay setting the subject to give ContactForm time to mount and populate availableSubjects
    setTimeout(() => {
      setContactFormSubjectToMissingOrder();
    }, 100);
  };

  const hasSubOrders = orderInfo && orderInfo.subOrders && orderInfo.subOrders.length > 0;
  const isInit = order.status === ORDER_STATUS.INIT || order.status === ORDER_STATUS.SUBMITTED;
  const isShippedWithDate = !!order.orderDate && (SHIPPED_STATES as readonly string[]).includes(order.status);

  return (
    <section className={styles.summary} aria-label="Order Status Summary" role="region">
      {(isInit || hasSubOrders || isShippedWithDate) && (
        <Text className={styles.title} variant={TEXT_VARIANT.HEADLINE}>
          {summaryHeadline}
        </Text>
      )}
      {order ? (
        hasSubOrders ? (
          orderInfo.subOrders.map((subOrder, index) => (
            <OrderStatusSummaryCard
              key={subOrder.orderNumber}
              subOrder={subOrder}
              summaries={summaries}
              productConstants={productConstants}
              constants={constants}
              theme={theme}
              index={index}
              totalSubOrders={orderInfo.subOrders.length}
              onContactClick={handleContactClick}
            />
          ))
        ) : isInit ? (
          <OrderStatusSummaryCard
            summaries={summaries}
            productConstants={productConstants}
            constants={constants}
            theme={theme}
            index={0}
            totalSubOrders={0}
            statusOverride={ORDER_STATUS.INIT}
            onContactClick={handleContactClick}
          />
        ) : isShippedWithDate ? (
          <OrderStatusSummaryCard
            summaries={summaries}
            productConstants={productConstants}
            constants={constants}
            theme={theme}
            index={0}
            totalSubOrders={0}
            statusOverride={order.status}
            onContactClick={handleContactClick}
          />
        ) : (
          // UNKNOWN order status
          <OrderStatusSummaryCard
            summaries={summaries}
            productConstants={productConstants}
            constants={constants}
            theme={theme}
            index={0}
            totalSubOrders={0}
            statusOverride={ORDER_STATUS.UNKNOWN}
            onContactClick={handleContactClick}
          />
        )
      ) : null}
    </section>
  );
}
